import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Search, ChevronRight, ChevronDown, BookOpen, Folder, FileText } from 'lucide-react';
import { FinancePageLayout } from '@/components/finance';
import { useChartOfAccounts, buildAccountHierarchy, type ChartOfAccount } from '@/hooks/finance/useChartOfAccounts';
import { cn } from '@/lib/utils';

const TYPE_LABELS: Record<string, string> = {
  revenue: 'Receita',
  expense: 'Despesa',
  asset: 'Ativo',
  liability: 'Passivo',
  equity: 'Patrimônio',
};

const TYPE_COLORS: Record<string, string> = {
  revenue: 'bg-green-100 text-green-800',
  expense: 'bg-red-100 text-red-800',
  asset: 'bg-blue-100 text-blue-800',
  liability: 'bg-orange-100 text-orange-800',
  equity: 'bg-purple-100 text-purple-800',
};

const BEHAVIOR_LABELS: Record<string, string> = {
  fixed: 'Fixo',
  variable: 'Variável',
  mixed: 'Misto',
};

const BEHAVIOR_COLORS: Record<string, string> = {
  fixed: 'bg-slate-100 text-slate-800',
  variable: 'bg-teal-100 text-teal-800',
  mixed: 'bg-amber-100 text-amber-800',
};

interface AccountNodeProps {
  account: ChartOfAccount;
  children: ChartOfAccount[];
  allAccounts: ChartOfAccount[];
  level: number;
  searchTerm: string;
}

function AccountNode({ account, children, allAccounts, level, searchTerm }: AccountNodeProps) {
  const [isOpen, setIsOpen] = useState(level < 2 || searchTerm.length > 0);
  const hasChildren = children.length > 0;
  
  const matchesSearch = searchTerm.length === 0 || 
    account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.name_pt?.toLowerCase().includes(searchTerm.toLowerCase());

  const childrenMatchSearch = searchTerm.length > 0 && children.some(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!matchesSearch && !childrenMatchSearch && searchTerm.length > 0) {
    return null;
  }

  return (
    <div className={cn("border-l-2 border-muted", level > 0 && "ml-4")}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div 
          className={cn(
            "flex items-center gap-2 py-2 px-3 hover:bg-muted/50 rounded-r transition-colors",
            matchesSearch && searchTerm.length > 0 && "bg-yellow-50"
          )}
        >
          {hasChildren ? (
            <CollapsibleTrigger className="flex items-center">
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </CollapsibleTrigger>
          ) : (
            <span className="w-4" />
          )}
          
          {hasChildren ? (
            <Folder className="h-4 w-4 text-muted-foreground" />
          ) : (
            <FileText className="h-4 w-4 text-muted-foreground" />
          )}
          
          <span className="font-mono text-sm text-muted-foreground">{account.code}</span>
          <span className="font-medium">{account.name}</span>
          {account.name_pt && account.name_pt !== account.name && (
            <span className="text-sm text-muted-foreground">({account.name_pt})</span>
          )}
          
          <div className="flex-1" />
          
          <Badge variant="secondary" className={TYPE_COLORS[account.type]}>
            {TYPE_LABELS[account.type] || account.type}
          </Badge>
          
          {account.cost_behavior && (
            <Badge variant="outline" className={BEHAVIOR_COLORS[account.cost_behavior]}>
              {BEHAVIOR_LABELS[account.cost_behavior]}
            </Badge>
          )}
        </div>
        
        {hasChildren && (
          <CollapsibleContent>
            {children.map(child => {
              const grandchildren = allAccounts.filter(a => a.parent_code === child.code);
              return (
                <AccountNode
                  key={child.id}
                  account={child}
                  children={grandchildren}
                  allAccounts={allAccounts}
                  level={level + 1}
                  searchTerm={searchTerm}
                />
              );
            })}
          </CollapsibleContent>
        )}
      </Collapsible>
    </div>
  );
}

export default function ChartOfAccounts() {
  const [searchTerm, setSearchTerm] = useState('');
  const { data: accounts, isLoading } = useChartOfAccounts();

  // Get root accounts (no parent)
  const rootAccounts = accounts?.filter(a => !a.parent_code) || [];

  // Count by type
  const countsByType = accounts?.reduce((acc, a) => {
    acc[a.type] = (acc[a.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};

  return (
    <FinancePageLayout>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Plano de Contas</h1>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Contas</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{accounts?.length || 0}</div>
          </CardContent>
        </Card>

        {Object.entries(countsByType).map(([type, count]) => (
          <Card key={type}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{TYPE_LABELS[type] || type}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{count}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar conta..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Account Tree */}
      <Card>
        <CardContent className="p-4">
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : rootAccounts.length === 0 ? (
            <p className="text-center py-10 text-muted-foreground">
              Nenhuma conta cadastrada
            </p>
          ) : (
            <div className="space-y-1">
              {rootAccounts.map(account => {
                const children = accounts?.filter(a => a.parent_code === account.code) || [];
                return (
                  <AccountNode
                    key={account.id}
                    account={account}
                    children={children}
                    allAccounts={accounts || []}
                    level={0}
                    searchTerm={searchTerm}
                  />
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </FinancePageLayout>
  );
}
